/**
 * Utility functions for Telegram WebApp compatibility
 * Using official @twa-dev/sdk for better type safety and compatibility
 */

import WebApp from '@twa-dev/sdk';

// Extended WebApp type with newer API properties (Bot API 7.7+)
interface ExtendedWebApp {
  requestFullscreen?: () => void;
  exitFullscreen?: () => void;
  isFullscreen?: boolean;
  // Swipe behavior control (Bot API 7.7+)
  disableVerticalSwipes?: () => void;
  enableVerticalSwipes?: () => void;
  isVerticalSwipesEnabled?: boolean;
  // Closing confirmation (Bot API 6.2+)
  enableClosingConfirmation?: () => void;
  disableClosingConfirmation?: () => void;
  isClosingConfirmationEnabled?: boolean;
  // Event handling
  onEvent?: (eventType: string, callback: () => void) => void;
  offEvent?: (eventType: string, callback: () => void) => void;
}

// Cast WebApp to extended type for accessing newer properties
const ExtWebApp = WebApp as typeof WebApp & ExtendedWebApp;

/**
 * Инициализация Login Widget данных
 * Сохраняем данные в отдельном объекте, не трогая window.Telegram.WebApp
 * (он может иметь read-only свойства от telegram-web-app.js)
 */
const initializeLoginWidgetCompat = (): void => {
  // Если уже есть WebApp initData - ничего не делаем
  if ((window as any).Telegram?.WebApp?.initData) {
    return;
  }
  
  // Проверяем данные от Login Widget в localStorage
  const storedAuth = localStorage.getItem('telegram_auth_data');
  if (!storedAuth) {
    return;
  }
  
  // Сохраняем данные Login Widget в отдельном объекте
  // Не пытаемся перезаписывать window.Telegram.WebApp (там могут быть getters)
  (window as any).__telegramLoginData = storedAuth;
  
  // Парсим данные пользователя
  try {
    const params = new URLSearchParams(storedAuth);
    (window as any).__telegramLoginUser = {
      id: parseInt(params.get('id') || '0'),
      first_name: params.get('first_name') || '',
      last_name: params.get('last_name') || undefined,
      username: params.get('username') || undefined,
      photo_url: params.get('photo_url') || undefined,
      language_code: 'ru',
      is_premium: false,
    };
  } catch (e) {
    // Игнорируем ошибки парсинга
  }
};

// Инициализируем совместимость с Login Widget сразу при загрузке модуля
initializeLoginWidgetCompat();

/**
 * Проверить, находимся ли мы внутри Telegram WebApp (Mini App)
 * Возвращает true только если приложение запущено внутри Telegram
 */
export const isInsideTelegramWebApp = (): boolean => {
  try {
    // Проверяем класс на html - он устанавливается в index.html ДО загрузки React
    if (document.documentElement.classList.contains('is-website')) {
      return false;
    }
    if (document.documentElement.classList.contains('is-telegram')) {
      return true;
    }
    
    // Fallback: проверяем наличие initData - это главный признак запуска внутри Telegram
    const hasInitData = !!WebApp.initData && WebApp.initData.length > 0;
    
    // Проверяем наличие user в initDataUnsafe
    const hasUser = !!(WebApp.initDataUnsafe?.user?.id);
    
    // Проверяем platform - только конкретные Telegram платформы
    const telegramPlatforms = ['android', 'ios', 'android_x', 'tdesktop', 'macos', 'weba', 'webk', 'unigram'];
    const isTelegramPlatform = telegramPlatforms.includes(WebApp.platform);
    
    return hasInitData || hasUser || isTelegramPlatform;
  } catch {
    return false;
  }
};

/**
 * Check if Telegram WebApp is available and ready
 */
export const isTelegramWebAppReady = (): boolean => {
  return WebApp.isExpanded || false;
};

/**
 * Get Telegram WebApp version
 */
export const getTelegramVersion = (): string => {
  return WebApp.version || '1.0';
};

/**
 * Определить, является ли платформа десктопной
 * Десктоп: macos, windows, linux, web, tdesktop
 * Мобильная: всё остальное (android, ios, android_x и т.д.)
 */
export const isDesktopPlatform = (): boolean => {
  const platform = (WebApp.platform || '').toLowerCase();
  return /^(macos|windows|linux|web|tdesktop)$/i.test(platform);
};

/**
 * Initialize Telegram WebApp (call this once at app startup)
 * Включает:
 * - ready() + expand()
 * - disableVerticalSwipes() — блокировка случайного закрытия свайпом
 * - enableClosingConfirmation() — подтверждение при закрытии
 * - requestFullscreen() на мобильных платформах (с fallback на expand())
 * - Подписка на fullscreenChanged для CSS-класса на body
 */
let isInitialized = false;
export const initializeTelegramWebApp = (): void => {
  // Предотвращаем повторную инициализацию
  if (isInitialized) {
    return;
  }
  
  // Повторно проверяем Login Widget совместимость
  initializeLoginWidgetCompat();
  
  // Вызываем Telegram-специфичные методы ТОЛЬКО если мы внутри Telegram WebApp
  // Это предотвращает бесполезные postEvent сообщения в консоли браузера
  if (isInsideTelegramWebApp()) {
    try {
      // 1. Базовая инициализация
      WebApp.ready();
      WebApp.expand();
      
      // 2. Блокировка случайного закрытия свайпом вниз (Bot API 7.7+)
      if (ExtWebApp.disableVerticalSwipes) {
        ExtWebApp.disableVerticalSwipes();
      }
      
      // 3. Подтверждение при закрытии приложения (Bot API 6.2+)
      if (ExtWebApp.enableClosingConfirmation) {
        ExtWebApp.enableClosingConfirmation();
      }
      
      // 4. Полноэкранный режим на мобильных платформах
      const isMobile = !isDesktopPlatform();
      if (isMobile) {
        try {
          if (ExtWebApp.requestFullscreen) {
            ExtWebApp.requestFullscreen();
          }
        } catch {
          // Fallback: если requestFullscreen не удался — оставляем expand()
          try {
            WebApp.expand();
          } catch {
            // Игнорируем
          }
        }
      }
      
      // 5. Подписка на fullscreenChanged — переключение CSS-класса на body и html
      if (ExtWebApp.onEvent) {
        ExtWebApp.onEvent('fullscreenChanged', syncFullscreenClass);
        // Начальная синхронизация
        syncFullscreenClass();
      }
    } catch (error) {
      // Игнорируем ошибки
    }
  }
  
  isInitialized = true;
};

/**
 * Синхронизация CSS-класса fullscreen на document.body и document.documentElement
 * Вызывается при событии fullscreenChanged и при инициализации
 */
const syncFullscreenClass = (): void => {
  const isFs = ExtWebApp.isFullscreen || false;
  document.body.classList.toggle('fullscreen', isFs);
  document.documentElement.classList.toggle('is-fullscreen', isFs);
};

/**
 * Get Telegram initData for authentication
 * Приоритет: WebApp.initData > localStorage > пустая строка
 */
export const getInitData = (): string => {
  // Сначала пробуем официальный WebApp
  if (WebApp.initData) {
    return WebApp.initData;
  }
  
  // Затем данные из Login Widget (сохранённые в localStorage)
  const storedAuth = localStorage.getItem('telegram_auth_data');
  if (storedAuth) {
    return storedAuth;
  }
  
  return '';
};

/**
 * Запросить полноэкранный режим (новый API Telegram 7.7+)
 */
export const requestFullscreen = (): void => {
  // Только внутри Telegram WebApp
  if (!isInsideTelegramWebApp()) return;
  
  try {
    if (ExtWebApp.requestFullscreen) {
      ExtWebApp.requestFullscreen();
    }
  } catch (error) {
    // Игнорируем ошибки
  }
};

/**
 * Выйти из полноэкранного режима
 */
export const exitFullscreen = (): void => {
  // Только внутри Telegram WebApp
  if (!isInsideTelegramWebApp()) return;
  
  try {
    if (ExtWebApp.exitFullscreen) {
      ExtWebApp.exitFullscreen();
    }
  } catch (error) {
    // Игнорируем ошибки
  }
};

/**
 * Проверить, находится ли приложение в полноэкранном режиме
 */
export const isFullscreen = (): boolean => {
  return ExtWebApp.isFullscreen || false;
};

/**
 * Отключить вертикальные свайпы (предотвращает сворачивание приложения)
 * Используется при открытии модальных окон, где нужны жесты перетаскивания
 * Bot API 7.7+
 */
export const disableVerticalSwipes = (): void => {
  // Только внутри Telegram WebApp
  if (!isInsideTelegramWebApp()) return;
  
  try {
    if (ExtWebApp.disableVerticalSwipes) {
      ExtWebApp.disableVerticalSwipes();
    }
  } catch (error) {
    // Игнорируем ошибки - старые версии могут не поддерживать
  }
};

/**
 * Включить вертикальные свайпы обратно
 * Вызывается при закрытии модальных окон
 * Bot API 7.7+
 */
export const enableVerticalSwipes = (): void => {
  // Только внутри Telegram WebApp
  if (!isInsideTelegramWebApp()) return;
  
  try {
    if (ExtWebApp.enableVerticalSwipes) {
      ExtWebApp.enableVerticalSwipes();
    }
  } catch (error) {
    // Игнорируем ошибки
  }
};

/**
 * Проверить, включены ли вертикальные свайпы
 */
export const isVerticalSwipesEnabled = (): boolean => {
  return ExtWebApp.isVerticalSwipesEnabled ?? true;
};
